import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Download, AlertCircle, Clock, CheckCircle, X, ChevronLeft, ChevronRight, Printer, Check, Phone, Mail, Building } from 'lucide-react';

const PublicDevisPage = () => {
  const { token } = useParams();
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showRefuseModal, setShowRefuseModal] = useState(false);
  const [refuseReason, setRefuseReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadDevis();
  }, [token]);

  const loadDevis = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/public/devis/${token}`);
      
      if (response.ok) {
        const data = await response.json();
        setDevis(data);
      } else {
        const errorData = await response.json();
        if (response.status === 410) {
          setError({ type: 'expired', message: errorData.detail || 'Ce lien a expiré' });
        } else if (response.status === 404) {
          setError({ type: 'notfound', message: errorData.detail || 'Devis non trouvé' });
        } else {
          setError({ type: 'error', message: errorData.detail || 'Erreur lors du chargement' });
        }
      }
    } catch (err) {
      setError({ type: 'error', message: 'Impossible de charger le devis' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/devis/${token}/documents/${documentId}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'document';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        alert('Erreur lors du téléchargement');
      }
    } catch (err) {
      alert('Erreur lors du téléchargement');
    }
  };

  const handleDownloadAdditional = async (documentId, additionalDocId, filename) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/devis/${token}/documents/${documentId}/additional/${additionalDocId}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'document';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        alert('Erreur lors du téléchargement');
      }
    } catch (err) {
      alert('Erreur lors du téléchargement');
    }
  };

  const handlePrint = (documentId) => {
    const url = `${API_BASE_URL}/api/public/devis/${token}/documents/${documentId}/download`;
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => printWindow.print();
    }
  };

  const handleAccept = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/public/devis/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        alert('✓ Devis accepté avec succès !');
        loadDevis();
        setCurrentStep(0);
      } else {
        const errorData = await response.json();
        alert(`Erreur: ${errorData.detail || 'Impossible d\'accepter le devis'}`);
      }
    } catch (err) {
      alert('Erreur lors de l\'acceptation du devis');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefuse = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/public/devis/${token}/refuse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: refuseReason })
      });
      
      if (response.ok) {
        alert('Devis refusé');
        setShowRefuseModal(false);
        setRefuseReason('');
        loadDevis();
        setCurrentStep(0);
      } else {
        const errorData = await response.json();
        alert(`Erreur: ${errorData.detail || 'Impossible de refuser le devis'}`);
      }
    } catch (err) {
      alert('Erreur lors du refus du devis');
    } finally {
      setActionLoading(false);
    }
  };

  const getPreviewUrl = (documentId) => {
    return `${API_BASE_URL}/api/public/devis/${token}/documents/${documentId}/download`;
  };

  const canTakeAction = () => {
    return !devis?.status || devis.status === 'pending' || devis.status === 'en_attente';
  };

  const totalSteps = devis?.documents?.length || 0;
  const isInfoStep = currentStep === 0;
  const isDocStep = currentStep > 0 && currentStep <= totalSteps;
  const isDecisionStep = currentStep > totalSteps;
  const currentDocIndex = currentStep - 1;
  const currentDoc = isDocStep ? devis?.documents?.[currentDocIndex] : null;

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            error.type === 'expired' ? 'bg-amber-100' : 'bg-red-100'
          }`}>
            {error.type === 'expired' ? (
              <Clock size={32} className="text-amber-600" />
            ) : (
              <AlertCircle size={32} className="text-red-600" />
            )}
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-3">
            {error.type === 'expired' ? 'Devis expiré' : 'Devis non trouvé'}
          </h1>
          {error.type === 'expired' ? (
            <div className="text-slate-600 space-y-3">
              <p>
                Votre devis a expiré. Les tarifs proposés ne sont plus garantis.
              </p>
              <p className="font-medium text-slate-700">
                Nous vous invitons à contacter votre courtier pour obtenir une nouvelle proposition actualisée.
              </p>
            </div>
          ) : (
            <p className="text-slate-600">{error.message}</p>
          )}
        </div>
      </div>
    );
  }

  // Si devis déjà traité
  if (!canTakeAction()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          {/* Logo ou nom du courtier */}
          {devis.courtier?.logo_url ? (
            <img 
              src={`${API_BASE_URL}${devis.courtier.logo_url}`}
              alt="Logo"
              className="h-16 max-w-[200px] object-contain mx-auto mb-4"
            />
          ) : devis.courtier?.cabinet_name ? (
            <p className="text-lg font-semibold text-slate-700 mb-4">{devis.courtier.cabinet_name}</p>
          ) : null}
          
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            devis.status === 'accepted' ? 'bg-emerald-100' : 'bg-red-100'
          }`}>
            {devis.status === 'accepted' ? (
              <CheckCircle size={32} className="text-emerald-600" />
            ) : (
              <X size={32} className="text-red-600" />
            )}
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">
            {devis.status === 'accepted' ? 'Devis accepté' : 'Devis refusé'}
          </h1>
          <p className="text-slate-600">
            {devis.status === 'accepted' 
              ? `Vous avez accepté ce devis le ${devis.accepted_at ? new Date(devis.accepted_at).toLocaleDateString('fr-FR') : ''}`
              : `Vous avez refusé ce devis le ${devis.refused_at ? new Date(devis.refused_at).toLocaleDateString('fr-FR') : ''}`
            }
          </p>
          {devis.refuse_reason && (
            <p className="text-sm text-slate-500 mt-2">Motif: {devis.refuse_reason}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex flex-col">
      {/* Header simple */}
      <header className="bg-gradient-to-r from-[#f97316] to-[#fbbf24] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="font-bold text-lg">Consultation du devis</h1>
          {/* Progress indicator */}
          <div className="flex items-center space-x-2">
            {[...Array(totalSteps + 2)].map((_, i) => (
              <div
                key={i}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  i === currentStep ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Main Content avec sidebar courtier */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          
          {/* Bloc Courtier - Gauche */}
          <div className="lg:w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
              {/* Logo */}
              {devis.courtier?.logo_url ? (
                <div className="w-24 h-24 bg-slate-100 rounded-xl flex items-center justify-center overflow-hidden p-2 mx-auto mb-4">
                  <img 
                    src={`${API_BASE_URL}${devis.courtier.logo_url}`}
                    alt="Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-24 h-24 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Building size={40} className="text-orange-600" />
                </div>
              )}
              
              {/* Nom du cabinet */}
              <h2 className="text-xl font-bold text-slate-900 text-center mb-4">
                {devis.courtier?.cabinet_name || 'Votre courtier'}
              </h2>
              
              {/* Coordonnées */}
              <div className="space-y-3 text-sm">
                {devis.courtier?.phone && (
                  <div className="flex items-center space-x-3 text-slate-600">
                    <Phone size={18} className="text-orange-500 flex-shrink-0" />
                    <span>{devis.courtier.phone}</span>
                  </div>
                )}
                {devis.courtier?.email && (
                  <div className="flex items-center space-x-3 text-slate-600">
                    <Mail size={18} className="text-orange-500 flex-shrink-0" />
                    <span className="break-all">{devis.courtier.email}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Contenu principal - Droite */}
          <div className="flex-1">
        
        {/* ÉTAPE 0 : Informations générales */}
        {isInfoStep && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText size={32} className="text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">{devis.title || 'Votre devis'}</h2>
              {devis.client_name && (
                <p className="text-slate-500 mt-1">Pour {devis.client_name}</p>
              )}
            </div>
            
            {/* Message */}
            {devis.description && (
              <div className="bg-slate-50 rounded-xl p-4 sm:p-6">
                <label className="block text-sm text-slate-500 mb-2">Message de votre courtier</label>
                <div
                  className="text-slate-700 [&_h2]:font-bold [&_h2]:text-base [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:font-bold [&_h3]:text-sm [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_li]:mb-0.5 [&_strong]:font-bold [&_table]:w-full [&_table]:border-collapse [&_table]:my-2 [&_td]:border [&_td]:border-slate-300 [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-slate-300 [&_th]:px-2 [&_th]:py-1 [&_th]:bg-slate-100 [&_th]:font-semibold"
                  dangerouslySetInnerHTML={{ __html: devis.description }}
                />
              </div>
            )}
            
            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start sm:items-center space-x-3">
              <FileText size={24} className="text-blue-600 flex-shrink-0 mt-0.5 sm:mt-0" />
              <p className="text-slate-700">
                Ce devis contient <span className="font-bold text-orange-600">{totalSteps} proposition(s)</span> d'assurance à consulter.
              </p>
            </div>
            
            {/* Meta */}
            <div className="flex flex-wrap items-center justify-between text-sm text-slate-500 gap-2">
              <span>Créé le {devis.created_at ? new Date(devis.created_at).toLocaleDateString('fr-FR') : '-'}</span>
              {devis.expires_at && (
                <span className="flex items-center space-x-1">
                  <Clock size={14} />
                  <span>Expire le {new Date(devis.expires_at).toLocaleDateString('fr-FR')}</span>
                </span>
              )}
            </div>
            
            {/* Bouton */}
            <button
              onClick={() => setCurrentStep(1)}
              disabled={totalSteps === 0}
              className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all font-semibold shadow-lg disabled:opacity-50 text-lg"
            >
              <span>Voir les propositions</span>
              <ChevronRight size={22} />
            </button>
          </div>
        )}

        {/* ÉTAPE 1-N : Documents */}
        {isDocStep && currentDoc && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Document Header */}
            <div className="p-4 sm:p-6 border-b border-slate-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <FileText size={24} className="text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Document {currentDocIndex + 1}</h3>
                    <p className="text-sm text-slate-500">{currentDoc.original_filename}</p>
                    {currentDoc.file_size && (
                      <p className="text-xs text-slate-400">{formatFileSize(currentDoc.file_size)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePrint(currentDoc.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
                  >
                    <Printer size={16} />
                    <span className="hidden sm:inline">Imprimer</span>
                  </button>
                  <button
                    onClick={() => handleDownload(currentDoc.id, currentDoc.original_filename)}
                    className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
                  >
                    <Download size={16} />
                    <span>Télécharger</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Document Viewer */}
            <div className="bg-slate-100">
              {currentDoc.original_filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img 
                  src={getPreviewUrl(currentDoc.id)}
                  alt={currentDoc.original_filename}
                  className="w-full max-h-[50vh] sm:max-h-[60vh] object-contain"
                />
              ) : currentDoc.original_filename?.match(/\.(pdf)$/i) ? (
                <iframe
                  src={`${getPreviewUrl(currentDoc.id)}#toolbar=1&navpanes=0`}
                  className="w-full h-[50vh] sm:h-[60vh]"
                  title={`Aperçu ${currentDoc.original_filename}`}
                />
              ) : (
                <object
                  data={getPreviewUrl(currentDoc.id)}
                  type={currentDoc.mime_type || "application/pdf"}
                  className="w-full h-[50vh] sm:h-[60vh]"
                >
                  <div className="flex items-center justify-center h-[50vh] text-slate-500">
                    <div className="text-center p-8">
                      <FileText size={48} className="mx-auto text-slate-300 mb-3" />
                      <p>Aperçu non disponible</p>
                      <p className="text-sm">Cliquez sur Télécharger pour voir le document</p>
                    </div>
                  </div>
                </object>
              )}
            </div>
            
            {/* Documents supplémentaires (CG, IPID, etc.) */}
            {currentDoc.additional_documents && currentDoc.additional_documents.length > 0 && (
              <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center space-x-2">
                  <FileText size={16} className="text-slate-500" />
                  <span>Documents supplémentaires (CG, IPID, etc.)</span>
                </h4>
                <div className="space-y-2">
                  {currentDoc.additional_documents.map((addDoc) => (
                    <div 
                      key={addDoc.id}
                      className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200"
                    >
                      <div className="flex items-center space-x-3 min-w-0">
                        <FileText size={18} className="text-blue-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{addDoc.original_filename}</p>
                          {addDoc.file_size && (
                            <p className="text-xs text-slate-400">{formatFileSize(addDoc.file_size)}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownloadAdditional(currentDoc.id, addDoc.id, addDoc.original_filename)}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm flex-shrink-0"
                      >
                        <Download size={14} />
                        <span className="hidden sm:inline">Télécharger</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="p-4 sm:p-6 space-y-4">
              {/* Accept Button */}
              <button
                onClick={handleAccept}
                disabled={actionLoading}
                className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all font-semibold shadow-lg disabled:opacity-50 text-lg"
              >
                <Check size={22} />
                <span>Accepter ce devis</span>
              </button>
              
              {/* Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="flex items-center space-x-1 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
                >
                  <ChevronLeft size={18} />
                  <span>Précédent</span>
                </button>
                
                {/* Mobile Progress */}
                <div className="flex items-center space-x-2 sm:hidden">
                  {devis.documents.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full ${
                        index === currentDocIndex ? 'bg-orange-500' : 'bg-slate-300'
                      }`}
                    />
                  ))}
                </div>
                
                {currentStep < totalSteps ? (
                  <button
                    onClick={() => setCurrentStep(currentStep + 1)}
                    className="flex items-center space-x-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    <span>Suivant</span>
                    <ChevronRight size={18} />
                  </button>
                ) : (
                  <button
                    onClick={() => setCurrentStep(totalSteps + 1)}
                    className="flex items-center space-x-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    <Check size={18} />
                    <span>Terminer</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ÉTAPE FINALE : Décision */}
        {isDecisionStep && (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto">
              <FileText size={32} className="text-slate-600" />
            </div>
            
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Que souhaitez-vous faire ?</h3>
              <p className="text-slate-600">
                Vous avez terminé la consultation. Vous pouvez prendre une décision maintenant ou revenir plus tard.
              </p>
            </div>
            
            <div className="space-y-3 max-w-md mx-auto">
              <button
                onClick={() => setShowRefuseModal(true)}
                disabled={actionLoading}
                className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-semibold disabled:opacity-50"
              >
                <X size={20} />
                <span>Refuser le devis</span>
              </button>
              
              <button
                onClick={() => {
                  window.close();
                  // Si window.close() ne fonctionne pas (onglet ouvert manuellement)
                  setTimeout(() => {
                    alert('Vous pouvez fermer cet onglet. Votre devis reste accessible via le même lien.');
                  }, 100);
                }}
                className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-slate-600 text-white rounded-xl hover:bg-slate-700 transition-all font-semibold"
              >
                <Clock size={20} />
                <span>Conserver et voir plus tard</span>
              </button>
            </div>
            
            <button
              onClick={() => setCurrentStep(totalSteps)}
              className="text-slate-500 hover:text-slate-700 text-sm underline"
            >
              ← Revenir aux documents
            </button>
          </div>
        )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4">
        <div className="max-w-5xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} ASAR Conseils Solutions Gestion</p>
        </div>
      </footer>

      {/* Modal Refus */}
      {showRefuseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-5 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <X size={24} />
                <h3 className="font-bold text-lg">Refuser le devis</h3>
              </div>
              <button
                onClick={() => setShowRefuseModal(false)}
                className="p-1 hover:bg-white/20 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-slate-600 mb-4">
                Veuillez indiquer le motif de votre refus :
              </p>
              <textarea
                value={refuseReason}
                onChange={(e) => setRefuseReason(e.target.value)}
                placeholder="Saisissez le motif de votre refus..."
                className="w-full p-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                rows={5}
                autoFocus
              />
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  setShowRefuseModal(false);
                  setRefuseReason('');
                }}
                disabled={actionLoading}
                className="px-5 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleRefuse}
                disabled={actionLoading || !refuseReason.trim()}
                className="flex items-center justify-center space-x-2 px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50"
              >
                {actionLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Check size={18} />
                )}
                <span>Confirmer</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicDevisPage;
